import { Expose, Transform, Type } from 'class-transformer';
import { UserEntity } from '@core/entities/user.entity';
import { PaginatedEntity } from '@core/entities/_extra/paginated.entity';
import { BaseEntity } from '@core/entities/_extra/base.entity';
import { InitializableEntity } from '@core/entities/_extra/intializable.entity';
import { GetUserId } from '@core/utils';
import { SafeUrl } from '@angular/platform-browser';
import { ApprovedStatus, GroupPrivate, TaskProgressStatus } from '@core/enums';
import { TaskEntity } from '@core/entities';

export class GroupsList extends PaginatedEntity {
  @Type(() => GroupEntity)
  groups: GroupEntity[];
}

export class GroupEntity extends BaseEntity<GroupEntity> {
  currentUserId = GetUserId();

  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  title = '';

  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  name = '';

  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  description = '';

  isPrivate: GroupPrivate;

  @Expose({ name: 'posts' })
  postsCount: number;

  newsCount: number;

  // @Expose({ name: 'participantCount' })
  participantsCount: number;

  taskGroupCount: number;

  eventGroupCount: number;

  @Type(() => GroupParticipant)
  participantsApproved: GroupParticipant[];

  @Type(() => GroupParticipant)
  participants?: GroupParticipant[] = [];

  newsGroup: any[];
  image: string;

  // For UI use only
  imageUrl: string | SafeUrl;

  // For POST/PUT Request
  @Expose({ name: 'keycloak_id' })
  keycloakId = '';

  @Expose({ name: 'group_creator' })
  @Type(() => UserEntity)
  groupCreator: UserEntity = null;

  @Expose({ name: 'groupTask' })
  @Type(() => TaskEntity)
  groupTasks?: TaskEntity[]; // for count in group details

  /**
   * To store multiple group admins user IDs for PUT/POST request
   */
  @Expose()
  @Type(() => GroupParticipant)
  groupAdmin: Pick<GroupParticipant, 'userId'>[] = []; // NEW PROPERTY: to store multiple group admins user IDs for POST request

  /**
   * To get multiple group admins from GET request
   */
  @Expose({ name: 'group_admins' })
  @Type(() => GroupParticipant)
  groupAdmins: GroupParticipant[]; // NEW PROPERTY: to GET multiple group admins from GET request

  constructor(values?: Partial<GroupEntity>) {
    super();
    this.initEntity(values);
  }

  get isAdmin(): boolean {
    return this.createdByUser?.id === this.currentUserId || false;
  }

  get incompleteTasksCount(): number {
    return this.groupTasks?.filter((task) => task.status !== TaskProgressStatus.Completed).length;
  }

  get isMember() {
    return this.participants?.some((member) => member.userId === this.currentUserId) || false;
  }

  get isApprovedMember(): boolean {
    return this.participants?.some((member) => member.userId === this.currentUserId && member.approvedStatus === ApprovedStatus.Approved) || false;
  }

  get approvedParticipantsCount(): number {
    return this.participants?.filter((member) => member.approvedStatus === ApprovedStatus.Approved).length;
  }

  get approvedParticipants() {
    return this.participantsApproved && this.participantsApproved.length ? this.participantsApproved : this.participants?.filter((member) => member.approvedStatus === ApprovedStatus.Approved) || [];
  }
}

export class GroupParticipant extends InitializableEntity {
  id?: number;

  @Expose({ name: 'user_id' })
  userId: number;

  @Expose({ name: 'group_id' })
  groupId?: number;

  @Expose({ name: 'approved_status' })
  approvedStatus: number;

  @Expose({ name: 'read_status' })
  readStatus?: number;

  @Expose({ name: 'group_updated_status' })
  groupUpdatedStatus?: number;

  @Type(() => UserEntity)
  user?: UserEntity;

  @Expose({ name: 'groupusers' })
  @Type(() => UserEntity)
  groupUser?: Partial<UserEntity>[];

  // FOr POST/PUT Request
  @Expose({ name: 'keycloak_id' })
  keycloakId = '';

  constructor(values?: Partial<GroupParticipant>) {
    super();
  }
}

export class GroupImage {
  @Expose({ name: 'group_id' })
  groupId: number;

  @Expose({ name: 'group_image' })
  groupImage: string;
}
